<template>
  <div
    :class="[
      $style.splitterResizer,
      {
        [$style.resizer]: isResizing,
        [$style.disabled]: disabled,
      },
    ]"
    @mousedown="startResize"
  >
    <div :class="$style.resizerHandle"></div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref } from "vue";

import type { SplitterContext } from "./splitterTypes";

const props = defineProps<{
  leftPanelId: string;
  rightPanelId: string;
  disabled?: boolean;
}>();

const splitterContext = inject<SplitterContext>("splitterContext");
const isResizing = ref(false);
const startPosition = ref(0);
const startLeftSize = ref(0);
const startRightSize = ref(0);
const RESIZING_BODY_CLASS = "SCASplitterResizer-active";
let pendingClientX: number | null = null;
let frameId: number | null = null;

const leftPanel = computed(() => splitterContext?.panels.find((p) => p.id === props.leftPanelId));
const rightPanel = computed(() => splitterContext?.panels.find((p) => p.id === props.rightPanelId));

const startResize = (event: MouseEvent) => {
  if (props.disabled || !splitterContext || !leftPanel.value || !rightPanel.value) {
    return;
  }

  isResizing.value = true;
  startPosition.value = event.clientX;

  startLeftSize.value = leftPanel.value.size ?? 0;
  startRightSize.value = rightPanel.value.size ?? 0;

  splitterContext.onResizeStart(props.leftPanelId);

  document.body.classList.add(RESIZING_BODY_CLASS);

  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", endResize);
};

// Mice report faster than the display refreshes; resizing more than once per frame only re-lays out the page.
const handleMouseMove = (event: MouseEvent) => {
  pendingClientX = event.clientX;
  frameId ??= requestAnimationFrame(flushPendingResize);
};

const flushPendingResize = () => {
  frameId = null;
  if (pendingClientX === null) return;
  const clientX = pendingClientX;
  pendingClientX = null;
  applyResize(clientX);
};

const applyResize = (currentPosition: number) => {
  if (!isResizing.value || !splitterContext || !leftPanel.value || !rightPanel.value) return;

  let delta = currentPosition - startPosition.value;

  const isLeftFlexible = leftPanel.value.size === undefined;
  const isRightFlexible = rightPanel.value.size === undefined;

  const leftMin = leftPanel.value.minSize ?? 0;
  const leftMax = leftPanel.value.maxSize ?? Infinity;
  const rightMin = rightPanel.value.minSize ?? 0;
  const rightMax = rightPanel.value.maxSize ?? Infinity;

  let newLeftSize = startLeftSize.value + delta;
  let newRightSize = startRightSize.value - delta;

  // Adjust delta according to min/max of fixed panels
  if (!isLeftFlexible) {
    if (newLeftSize > leftMax) {
      delta -= newLeftSize - leftMax;
      newLeftSize = leftMax;
      newRightSize = startRightSize.value - delta;
    }
    if (newLeftSize < leftMin) {
      delta += leftMin - newLeftSize;
      newLeftSize = leftMin;
      newRightSize = startRightSize.value - delta;
    }
  }

  if (!isRightFlexible) {
    if (newRightSize > rightMax) {
      delta -= newRightSize - rightMax;
      newRightSize = rightMax;
      newLeftSize = startLeftSize.value + delta;
    }
    if (newRightSize < rightMin) {
      delta += rightMin - newRightSize;
      newRightSize = rightMin;
      newLeftSize = startLeftSize.value + delta;
    }
  }

  // Limit total width of panels to container width
  const visiblePanels = splitterContext.panels.filter((p) => p.visible && !p.isOverlay);
  const splitterWidth = Math.max(visiblePanels.length - 1, 0) * 12;

  const totalWidth = visiblePanels.reduce((sum, panel) => {
    if (panel.id === leftPanel.value!.id)
      return sum + (isLeftFlexible ? (panel.size ?? panel.minSize ?? 0) : newLeftSize);
    if (panel.id === rightPanel.value!.id)
      return sum + (isRightFlexible ? (panel.size ?? panel.minSize ?? 0) : newRightSize);
    return sum + (panel.size ?? panel.minSize ?? 0);
  }, 0);

  const containerWidth = window.innerWidth - splitterWidth;
  if (totalWidth > containerWidth) {
    // Adjust fixed panels to prevent overflow
    const overflow = totalWidth - containerWidth;
    if (!isLeftFlexible) {
      newLeftSize -= overflow;
      if (newLeftSize < leftMin) newLeftSize = leftMin;
      newRightSize = startRightSize.value - (newLeftSize - startLeftSize.value);
    } else if (!isRightFlexible) {
      newRightSize -= overflow;
      if (newRightSize < rightMin) newRightSize = rightMin;
      newLeftSize = startLeftSize.value + (startRightSize.value - newRightSize);
    }
  }

  // Apply sizes only to fixed panels
  const sizes: Record<string, number> = {};
  if (!isLeftFlexible) sizes[props.leftPanelId] = newLeftSize;
  if (!isRightFlexible) sizes[props.rightPanelId] = newRightSize;
  splitterContext.updatePanelSizes(sizes);
};

const endResize = () => {
  if (!splitterContext) return;

  if (frameId !== null) {
    cancelAnimationFrame(frameId);
    flushPendingResize();
  }

  isResizing.value = false;
  splitterContext.onResizeEnd(props.leftPanelId);

  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", endResize);

  document.body.classList.remove(RESIZING_BODY_CLASS);
};

onBeforeUnmount(() => {
  if (isResizing.value) endResize();
});
</script>

<style lang="scss" module>
:global(.SCASplitterResizer-active),
:global(.SCASplitterResizer-active *) {
  cursor: col-resize !important;
  user-select: none !important;
}

.splitterResizer {
  position: relative;
  z-index: 10;
  background: transparent;

  user-select: none;
  -webkit-user-select: none; /* Safari */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */

  width: 12px;
  height: 100%;
  cursor: col-resize;

  &.disabled {
    cursor: not-allowed !important;
  }

  .resizerHandle {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 100%;
    transition: background-color 0.2s;
  }

  &:hover .resizerHandle,
  &.resizer .resizerHandle {
    background-color: var(--color-border-brand-primary);
  }
}
</style>
